import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { BuildTableData, VariantsContentProps } from './types';
import { SetType, TestSet } from '../types';
import { useIntl } from 'react-intl';
import { useSelectedFilterOptions } from '../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import { useActiveTest } from '../ActiveTestContext';
import { useFilterProcessingState } from '../FilterProcessingStateContext';
import { useSelectedSequenceType } from '../SequenceTypeContext';
import { useVariantCount } from '../VariantCountContext';
import { sharedFilterErrorMessages } from './sharedIntlLabels';
import { reduceFilters } from './utils';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { useSharedStyles } from './sharedStyles';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import {
  TableConfiguration,
  TableModule,
} from '@lifeomic/chroma-react/components/TableModule';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveAccountId';
import { KeysAndDirections } from '../useOrderByTableData';
import { AxiosError, AxiosResponse } from 'axios';
import { SelectedVariantsContext } from '../SelectedVariantsContext';

type VariantContentProps = VariantsContentProps & {
  setType: SetType;
  isLoading: boolean;
  hasMoreToLoad: boolean;
  loadMore: () => Promise<void>;
  sortedData: any[];
  handleSortClicked: (keysAndDirections: KeysAndDirections) => void;
  resetResults: () => void;
  makeFetchRequest: (params?: any) => void;
  lastResponse: AxiosResponse<any>;
  error: AxiosError<any>;
  buildTableData: (props: BuildTableData) => Array<TableConfiguration>;
  additionalFetchFilter?: (filters: any) => any;
};

const VariantContent: FC<VariantContentProps> = ({
  onRowClick,
  fetchPrependedColumns,
  setType,
  isLoading,
  hasMoreToLoad,
  loadMore,
  sortedData,
  handleSortClicked,
  resetResults,
  makeFetchRequest,
  lastResponse,
  error,
  buildTableData,
  additionalFetchFilter,
}) => {
  const classes = useSharedStyles({});
  const tableRef = useRef<HTMLTableElement | null>(null);
  const activeTestIdRef = useRef<string>(null);
  const variantSetIdRef = useRef<string>(null);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [variantSet, setVariantSet] = useState<TestSet>(null);
  // When filters are changed, we want to immediately update
  // our loading state, even though the fetch is debounced,
  // just so the input feels immediate to the user.
  const [isLocalStateLoading, setIsLocalStateLoading] =
    useState<boolean>(false);
  const [addNote] = useNotify();
  const { formatMessage } = useIntl();
  const activeAccountId = useActiveAccountId();
  const [activeTest] = useActiveTest();
  const [, setVariantCount] = useVariantCount();
  const [selectedFilterOptions] = useSelectedFilterOptions();
  const [selectedSequenceType] = useSelectedSequenceType();
  const [filterProcessingState] = useFilterProcessingState();
  const { showSelectedVariants, selectedVariants } = useContext(
    SelectedVariantsContext,
  );
  const [displayData, setDisplayData] = useState<any[]>();

  useEffect(() => {
    if (!showSelectedVariants) {
      setDisplayData(sortedData);
      return;
    }

    const selectedVariantIds = selectedVariants
      .filter(selectedVariant => selectedVariant.setType === setType)
      .map(selectedVariant => selectedVariant.variantId);

    const sortedSelectedVariants = sortedData.filter(data =>
      selectedVariantIds.includes(data.id),
    );

    setDisplayData(sortedSelectedVariants);
  }, [sortedData, showSelectedVariants, selectedVariants, setType]);

  useEffect(() => {
    const setId = activeTest?.sets?.find(
      set =>
        set.sequenceType === selectedSequenceType && set.setType === setType,
    );

    if (!setId) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setVariantSet(null);
      return;
    }

    setVariantSet(setId);
  }, [activeTest, selectedSequenceType, setType]);

  useEffect(() => {
    if (!variantSet) {
      return;
    }

    if (variantSetIdRef.current !== variantSet?.id) {
      resetResults();
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      variantSetIdRef.current = variantSet.id;
    }
  }, [resetResults, variantSet]);

  useEffect(() => {
    if (!variantSet) {
      return;
    }
    if (filterProcessingState !== 'COMPLETE') {
      return;
    }

    const filters = reduceFilters(selectedFilterOptions);

    setIsLocalStateLoading(true);

    const debouncedFetch = setTimeout(async () => {
      await makeFetchRequest({
        setId: variantSet.id,
        filters: additionalFetchFilter
          ? additionalFetchFilter(filters)
          : filters,
      });
      setIsLocalStateLoading(false);
    }, 500);

    return () => {
      clearTimeout(debouncedFetch);
      setIsLocalStateLoading(false);
    };
  }, [
    additionalFetchFilter,
    filterProcessingState,
    makeFetchRequest,
    selectedFilterOptions,
    variantSet,
  ]);

  useEffect(() => {
    setVariantCount(lastResponse?.data?.count);
  }, [lastResponse, setVariantCount]);

  useEffect(() => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      addNote(formatMessage(sharedFilterErrorMessages.genericError), 'error');
      return;
    }

    if (error?.response?.status === 504) {
      addNote(formatMessage(sharedFilterErrorMessages.timeoutError), 'error');
      return;
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (error?.response?.status >= 500) {
      addNote(formatMessage(sharedFilterErrorMessages.serverError), 'error');
      return;
    }
  }, [addNote, error, formatMessage]);

  // To prevent stale data from the previous active test being displayed,
  // we will clear the results if we detect the test changing.
  useEffect(() => {
    if (!activeTest) {
      return;
    }

    if (activeTestIdRef.current !== activeTest?.id) {
      resetResults();
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      activeTestIdRef.current = activeTest.id;
    }
  }, [activeTest, resetResults]);

  if (!activeTest || !variantSet) {
    return null;
  }

  return (
    <InfiniteScroll
      loading={isLoading}
      hasNextPage={hasMoreToLoad}
      loadMore={loadMore}
      scrollOffset={100}
      scrollContainer="parent"
    >
      <TableModule
        config={buildTableData({
          variantSet,
          activeSequence: activeTest,
          account: activeAccountId,
          build: activeTest.referenceSetId,
          fetchPrependedColumns,
          formatMessage,
          onSort: handleSortClicked,
          classes,
        })}
        data={displayData}
        isLoading={isLoading || isLocalStateLoading}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        onRowClick={onRowClick}
        maxCellWidth={2}
        ref={tableRef}
      />
    </InfiniteScroll>
  );
};

export default VariantContent;
